import { SearchModel } from './common';

/**
 * Tipos para Recebimento de Materiais conforme schema [Financial].[MaterialReceipt]
 *
 * A tabela MaterialReceipt armazena dados do cabeçalho do recebimento.
 * Itens e parcelas são sub-recursos gerenciados separadamente.
 */

// ─── Material Receipt Item Damage Photo ──────────────────────────

export interface MaterialReceiptItemDamagePhoto {
  material_receipt_item_damage_photo_id?: number;
  material_receipt_item_id: number;
  damage_photo_url?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

// ─── Material Receipt Item Received Photo (produto personalizado) ─

/** Foto do produto RECEBIDO, anexada pela logística quando o item é personalizado. */
export interface MaterialReceiptItemReceivedPhoto {
  material_receipt_item_received_photo_id?: number;
  material_receipt_item_id: number;
  photo_url?: string;
  created_at?: string;
  created_by?: string;
}

/** PV vinculado ao PC do item (exibição "PC #x → PV #y"). */
export interface PurchaseOrderSalesOrderLinkInfo {
  order_id: number;
  customer_name?: string;
  order_status?: string;
  is_priority: boolean;
  request_delivery_time?: string;
  scheduled_ship_date?: string;
}

// ─── Material Receipt Item ───────────────────────────────────────

export interface MaterialReceiptItem {
  material_receipt_item_id?: number;
  material_receipt_id?: number;
  description: string;
  sku?: string;           // SKU do produto (somente leitura, retornado pelo GET)
  supplier_code?: string; // Código do fornecedor (somente leitura, carregado de ProviderProductPrice)
  unit?: string;
  unit_value: number;
  quantity: number;
  product_id?: number;
  raw_material_id?: number;
  purchase_order_id?: number;
  purchase_order_number?: number; // Número do pedido de compra (retornado via JOIN pelo backend)
  order_id?: number;              // ID do pedido de venda (para recebimento de devoluções de cliente)
  damaged?: boolean;
  damage_notes?: string;
  damage_photo_url?: string;
  damage_photos?: MaterialReceiptItemDamagePhoto[]; // Fotos de avaria (tabela separada)
  // Produto personalizado: flags + fotos do recebimento + PVs do PC do item
  is_custom_product?: boolean;
  has_project_photo?: boolean; // foto do projeto no produto (proxy GET /products/:id/project-photo/file)
  received_photos?: MaterialReceiptItemReceivedPhoto[];
  purchase_order_sales_orders?: PurchaseOrderSalesOrderLinkInfo[];
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

/** Extensão local para rastrear linhas no formulário */
export interface MaterialReceiptItemRow extends MaterialReceiptItem {
  _tempId: string;
  _type: 'product' | 'raw_material';
  _sourceCode?: string;       // SKU do produto ou código da matéria-prima
  _damagePhotoFile?: File;    // Arquivo de foto de avaria pendente de upload (somente frontend) - DEPRECATED: use _damagePhotoFiles
  _damagePhotoFiles?: File[]; // Arquivos de fotos de avaria pendentes de upload (somente frontend)
  _receivedPhotoFiles?: File[]; // Fotos do recebimento (produto personalizado) pendentes de upload (somente frontend)
  _orderNum?: number;         // Número do pedido de venda (somente frontend, para exibição)
  _parentTempId?: string;     // ID do item pai (para itens de avaria criados a partir de um item)
}

// ─── Material Receipt Installment ────────────────────────────────

export interface MaterialReceiptInstallment {
  material_receipt_installment_id?: number;
  material_receipt_id?: number;
  value: number;
  due_date: string; // ISO date
  notes?: string;
  payment_method_id?: number;
  payment_method_name?: string;
  /** Cartão da empresa que paga a parcela — a provisão vai para a fatura dele. */
  credit_card_id?: number | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  last_updated_by?: string;
}

/** Extensão local para rastrear parcelas no formulário */
export interface MaterialReceiptInstallmentRow extends MaterialReceiptInstallment {
  _tempId: string;
}

// ─── Status ─────────────────────────────────────────────────────

/** 0 = Em conferência | 1 = Conferido */
export const MATERIAL_RECEIPT_STATUS = {
  IN_REVIEW: 0,
  CONFIRMED: 1,
} as const;

export type MaterialReceiptStatus = (typeof MATERIAL_RECEIPT_STATUS)[keyof typeof MATERIAL_RECEIPT_STATUS];

export const MATERIAL_RECEIPT_STATUS_LABELS: Record<MaterialReceiptStatus, string> = {
  [MATERIAL_RECEIPT_STATUS.IN_REVIEW]: 'Em conferência',
  [MATERIAL_RECEIPT_STATUS.CONFIRMED]: 'Conferido',
};

// ─── Material Receipt (cabeçalho) ────────────────────────────────

export interface MaterialReceipt {
  material_receipt_id: number;
  receipt_date?: string;
  provider_id?: number;
  provider_name?: string;
  provider_document?: string;
  customer_id?: number;
  customer_name?: string;
  notes?: string;
  internal_notes?: string;
  carrier?: string;
  volumes?: number;
  status: MaterialReceiptStatus;
  created_at: string;
  updated_at: string;
  created_by: string;
  last_updated_by: string;
  // sub-resources (retornado pelo GET :id)
  items?: MaterialReceiptItem[];
  installments?: MaterialReceiptInstallment[];
}

// ─── Form Data ───────────────────────────────────────────────────

export interface MaterialReceiptFormData {
  receipt_date?: string;
  provider_id?: number;
  customer_id?: number;
  notes?: string;
  internal_notes?: string;
  carrier?: string;
  volumes?: number;
  items: MaterialReceiptItemRow[];
  installments: MaterialReceiptInstallmentRow[];
}

// ─── Search Model ────────────────────────────────────────────────

export interface UpdateMaterialReceiptStatusRequest {
  status: MaterialReceiptStatus;
}

export interface MaterialReceiptSearchModel extends SearchModel {
  provider_id?: number;
  customer_id?: number;
  status?: MaterialReceiptStatus;
  date_from?: string;
  date_to?: string;
}

// ─── Purchase Order for selection ────────────────────────────────

export interface PurchaseOrderForReceipt {
  purchase_order_id: number;
  order_number?: number;
  order_date?: string;
  status: number;
  total?: number;
  is_priority?: boolean;
  sales_orders?: PurchaseOrderSalesOrderLinkInfo[]; // PVs atendidos pelo PC
  items: PurchaseOrderItemForReceipt[];
}

export interface PurchaseOrderItemForReceipt {
  purchase_order_item_id: number;
  description: string;
  sku?: string;           // SKU do produto (somente leitura, retornado pelo GET)
  supplier_code?: string; // Código do fornecedor (somente leitura, carregado de ProviderProductPrice)
  unit?: string;
  unit_value: number;
  quantity: number;
  product_id?: number;
  raw_material_id?: number;
  is_custom_product?: boolean; // habilita as fotos do recebimento
}

// ─── Damage report ───────────────────────────────────────────────

export interface DamageReport {
  item_description: string;
  item_code: string;
  damage_notes: string;
  damage_photo?: File;
}

// ─── Assign PO helper ───────────────────────────────────────────

export interface AssignPurchaseOrderSuggestion {
  item_temp_id: string;
  description: string;
  code: string;
  quantity: number;
  suggested_purchase_orders: {
    purchase_order_id: number;
    order_number?: number;
    order_date?: string;
    item_quantity: number;
  }[];
  selected_purchase_order_id?: number;
  split_quantity?: number;
}

// ─── Customer Order for Material Receipt ─────────────────────────

export interface CustomerOrderForReceipt {
  order_id: number;
  order_date: string;
  order_sell_total?: number;
  order_status?: string;
  items?: CustomerOrderItemForReceipt[];
  replacement_items?: ReplacementOrderItemForReceipt[];
}

export interface CustomerOrderItemForReceipt {
  order_item_id: number;
  sku: string;
  description?: string;
  quantity: number;
  returned_quantity?: number;
  unit_price?: number;
  product_id?: number;
}

export interface ReplacementOrderItemForReceipt {
  replacement_order_item_id: number;
  order_id: number;
  original_order_id: number;
  sku: string;
  description?: string;
  quantity: number;
  product_id?: number;
}

// ─── Material Receipt Order Update (Financial/Fiscal Management) ─

export interface MaterialReceiptOrderUpdate {
  material_receipt_id: number;
  order_id: number;
  is_return_invoice_generated: boolean;
  is_financial_reversal_applied: boolean;
  company_id?: number | null;
  invoice_id?: number | null;
}

export interface GenerateReturnInvoicePayload {
  invoice_transaction_type_id: number;
}
